if (!customElements.get("product-media-gallery")) {
  customElements.define(
    "product-media-gallery",
    class ProductMediaGallery extends HTMLElement {
      constructor() {
        super();
        this.swiper = null;
        this.init();
      }

      init() {
        this.thumbnails = this.querySelectorAll(".media-thumbnail");
        this.setupThumbnailClickHandlers();
        this.setupZoomTriggers();
        this.initSwiper();
      }

      setupZoomTriggers() {
        const zoomTriggers = this.querySelectorAll(".media-zoom-trigger");
        const zoomPopup = document.querySelector("product-media-zoom-popup");

        if (zoomPopup) {
          zoomTriggers.forEach((trigger) => {
            trigger.addEventListener("click", (event) => {
              // Don't open zoom if clicking on video controls, wishlist button, or 3D models
              if (
                event.target.closest("video") ||
                event.target.closest(".wishlist-btn") ||
                event.target.closest("button") ||
                event.target.closest("a") ||
                event.target.closest("model-viewer") ||
                trigger.dataset.mediaType === "model"
              ) {
                return;
              }

              const mediaId = trigger.dataset.mediaId;
              const slideIndex = parseInt(trigger.dataset.slideIndex);
              zoomPopup.open(mediaId, slideIndex);
            });
          });
        }
      }

      initSwiper() {
        let retries = 0;
        const maxRetries = 50; // Try for 5 seconds (50 * 100ms)

        const initSwiperWhenReady = () => {
          if (typeof Swiper !== "undefined") {
            this.swiper = new Swiper(
              this.querySelector(".product-media-swiper"),
              {
                // ... your existing Swiper config ...
                slidesPerView: 1,
                spaceBetween: 0,
                loop: false,
                allowTouchMove: false,
                navigation: {
                  nextEl: ".slideshow-nav-button-next",
                  prevEl: ".slideshow-nav-button-prev",
                },
                pagination: {
                  el: ".swiper-pagination",
                  clickable: true,
                  dynamicBullets: true,
                },
                lazy: {
                  loadPrevNext: true,
                  loadPrevNextAmount: 2,
                },
                on: {
                  slideChange: () => {
                    this.pauseAllMedia();
                    this.updateActiveThumbnail();
                    this.playCurrentSlideVideo();
                    this.updateNavButtons();
                  },
                  transitionStart: () => {
                    this.pauseAllMedia();
                  },
                  init: () => {
                    this.updateNavButtons();
                  },
                },
                watchOverflow: true,
                observer: true,
                observeParents: true,
              }
            );

            // Announce that the gallery is ready for commands
            this.dispatchEvent(
              new CustomEvent("gallery:ready", { bubbles: true })
            );
          } else {
            retries++;
            if (retries < maxRetries) {
              // Retry after a short delay if Swiper is not ready
              setTimeout(initSwiperWhenReady, 100);
            } else {
              console.error(
                "Product Media Gallery: Swiper library did not load in time."
              );
            }
          }
        };

        initSwiperWhenReady();
      }

      setupThumbnailClickHandlers() {
        this.thumbnails.forEach((thumbnail, index) => {
          thumbnail.addEventListener("click", (event) => {
            event.preventDefault();
            this.showMedia(thumbnail.dataset.mediaId, index);
          });
        });
      }

      showMedia(mediaId, slideIndex = null) {
        if (!mediaId) return;

        // If slideIndex is provided, use it; otherwise find the slide
        if (slideIndex !== null) {
          if (this.swiper) {
            this.swiper.slideTo(slideIndex);
          }
        } else {
          // Find the slide index by media ID
          const slides = this.querySelectorAll(".swiper-slide");
          slides.forEach((slide, index) => {
            if (slide.dataset.mediaId === mediaId) {
              if (this.swiper) {
                this.swiper.slideTo(index);
              }
            }
          });
        }

        this.updateActiveThumbnail(mediaId);
      }

      updateActiveThumbnail(mediaId = null) {
        // If no mediaId provided, get current slide's mediaId
        if (!mediaId && this.swiper) {
          const currentSlide = this.swiper.slides[this.swiper.activeIndex];
          mediaId = currentSlide?.dataset.mediaId;
        }

        // Update thumbnail active state
        this.thumbnails.forEach((thumb) => {
          const isActive = thumb.dataset.mediaId === mediaId;
          if (isActive) {
            thumb.classList.add("border-2", "border-solid", "border-brand");
          } else {
            thumb.classList.remove("border-2", "border-solid", "border-brand");
          }
        });
      }

      updateNavButtons() {
        if (!this.swiper) return;

        const prevBtn = this.querySelector(".slideshow-nav-button-prev");
        const nextBtn = this.querySelector(".slideshow-nav-button-next");

        if (prevBtn) {
          if (this.swiper.isBeginning) {
            prevBtn.classList.add("hidden");
          } else {
            prevBtn.classList.remove("hidden");
          }
        }

        if (nextBtn) {
          if (this.swiper.isEnd) {
            nextBtn.classList.add("hidden");
          } else {
            nextBtn.classList.remove("hidden");
          }
        }
      }

      playCurrentSlideVideo() {
        if (!this.swiper) return;

        const currentSlide = this.swiper.slides[this.swiper.activeIndex];
        if (currentSlide && currentSlide.dataset.mediaType === "video") {
          const video = currentSlide.querySelector("video");
          if (video) {
            video.play().catch((e) => console.error("Video play failed:", e));
          }
        }
      }

      pauseAllMedia() {
        this.querySelectorAll("video").forEach((video) => video.pause());
        this.querySelectorAll("iframe").forEach((iframe) => {
          const currentSrc = iframe.src;
          if (currentSrc.includes("youtube.com")) {
            // Use YouTube iframe API to pause video without reloading
            iframe.contentWindow.postMessage(
              '{"event":"command","func":"pauseVideo","args":""}',
              "*"
            );
          } else if (currentSrc.includes("vimeo.com")) {
            // Use Vimeo iframe API to pause video without reloading
            iframe.contentWindow.postMessage('{"method":"pause"}', "*");
          }
        });
      }

      // Public methods for external control
      nextSlide() {
        if (this.swiper) this.swiper.slideNext();
      }

      prevSlide() {
        if (this.swiper) this.swiper.slidePrev();
      }

      goToSlide(index) {
        if (this.swiper) this.swiper.slideTo(index);
      }

      // Method to be called by variant picker
      setActiveMediaById(mediaId) {
        if (!mediaId || !this.swiper) return false;

        const slides = this.querySelectorAll(".swiper-slide");
        let targetIndex = -1;

        slides.forEach((slide, index) => {
          if (slide.dataset.mediaId === mediaId.toString()) {
            targetIndex = index;
          }
        });

        if (targetIndex !== -1) {
          this.swiper.slideTo(targetIndex);
          this.updateActiveThumbnail(mediaId.toString());
          return true;
        }

        return false;
      }

      // Cleanup when element is removed
      disconnectedCallback() {
        if (this.swiper) {
          this.swiper.destroy();
        }
      }
    }
  );
}
